import { Link } from '@tanstack/react-router';

import type { ArAgingResponse } from '@dcc-erp/shared';
import { fmt, toNumber } from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

// Router-free AR aging report. The route owns data fetching + the asOfDate
// picker + xlsx export + customer-drill navigation; this component owns
// presentation only — a per-customer 5-bucket table with a grand-total footer
// row. Plain prototype.css classes only.

export interface AgingReportProps {
  data: ArAgingResponse;
  onExport: () => void;
  onCustomer: (customerId: string) => void;
}

export function AgingReport({ data, onExport, onCustomer }: AgingReportProps) {
  return (
    <div className="tbl-shell">
      <div
        className="filter-row"
        style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 0 }}
      >
        <button className="btn" onClick={onExport} disabled={data.customers.length === 0}>
          <Icon name="download" size={14} /> ส่งออก
        </button>
      </div>

      <div className="tbl-wrap" style={{ maxHeight: 600 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>ลูกค้า</th>
              <th className="num">ยังไม่ครบกำหนด</th>
              <th className="num">1-30 วัน</th>
              <th className="num">31-60 วัน</th>
              <th className="num">61-90 วัน</th>
              <th className="num">90+ วัน</th>
              <th className="num">รวม</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.customers.length === 0 ? (
              <tr>
                <td colSpan={8} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่มีข้อมูล
                </td>
              </tr>
            ) : (
              data.customers.map((c) => (
                <tr key={c.customerId}>
                  <td>
                    <button
                      type="button"
                      className="link-btn"
                      onClick={() => onCustomer(c.customerId)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        color: 'var(--brand)',
                        cursor: 'pointer',
                        font: 'inherit',
                        textAlign: 'left',
                      }}
                    >
                      <span className="code">{c.customerCode}</span> {c.customerName}
                    </button>
                  </td>
                  <td className="num tnum">{fmt(toNumber(c.notDue), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(c.d1_30), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(c.d31_60), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(c.d61_90), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(c.over90), 2)}</td>
                  <td className="num tnum">{fmt(toNumber(c.total), 2)}</td>
                  <td>
                    <Link
                      to="/ar/statement/$customerId"
                      params={{ customerId: c.customerId }}
                      className="link-btn"
                      style={{ color: 'var(--brand)' }}
                    >
                      ใบแจ้งยอด
                    </Link>
                  </td>
                </tr>
              ))
            )}
            <tr className="total">
              <td>รวมทั้งสิ้น</td>
              <td className="num tnum">{fmt(toNumber(data.totals.notDue), 2)}</td>
              <td className="num tnum">{fmt(toNumber(data.totals.d1_30), 2)}</td>
              <td className="num tnum">{fmt(toNumber(data.totals.d31_60), 2)}</td>
              <td className="num tnum">{fmt(toNumber(data.totals.d61_90), 2)}</td>
              <td className="num tnum">{fmt(toNumber(data.totals.over90), 2)}</td>
              <td className="num tnum">{fmt(toNumber(data.grandTotal), 2)}</td>
              <td />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
